import { CSSProperties, InputHTMLAttributes } from 'react';

import { appBorders } from '@backrs_ui/common-ui/src/assets/appBorders';
import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ReactComponent as OtpDecoration } from '@backrs_ui/common-ui/src/assets/images/otp-decoration.svg';
import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

import { BackrsForm } from '../BackrsForm/BackrsForm';
import { ResendCode } from '../BackrsForm/ResendCode';
import { BackrsFormField, BackrsFormValues } from '../BackrsForm/types';

const LOGIN_STRINGS = appStrings.modules.login;

const defaultOtpValues: BackrsFormValues = {
  code: '',
};

type OtpProps = Readonly<{
  handleOtpCode: (code: string) => void;
  errorMessage?: string;
  environmentUrl: string;
}>;

export const OneTimePassword = ({ handleOtpCode, errorMessage, environmentUrl }: OtpProps) => {
  const fields: BackrsFormField[] = [
    {
      id: 'code',
      name: 'code',
      placeholder: '######',
      type: 'tel',
      autoComplete: 'code',
      sx: otpInputStyle,
      inputProps: otpInputProps,
    },
  ];

  const handleSubmission = (data: BackrsFormValues) => {
    handleOtpCode(data.code);
  };

  return (
    <Box sx={containerStyle}>
      <BackrsIcon name="BackrsWithText" alt="backrs logo" style={responsiveBackrsLogoStyle} />
      <Box sx={loginFormStyle}>
        <Typography variant="h4" sx={otpPromptStyle}>
          {LOGIN_STRINGS.otp.title}
        </Typography>
        <BackrsForm
          fields={fields}
          defaultValues={defaultOtpValues}
          onSubmit={handleSubmission}
          buttonLabel={appStrings.buttonText.login}
        >
          <Box sx={otpContentWrapperStyle}>
            <ResendCode environmentUrl={environmentUrl} />
            <Box sx={otpDecorationContainerStyle}>
              <OtpDecoration width="100%" />
            </Box>
          </Box>
        </BackrsForm>
        {errorMessage && (
          <Box mt={2} color={appColors.color.red1}>
            {errorMessage}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 4,
} as const;

const loginFormStyle: SxProps<Theme> = {
  backgroundColor: appColors.neutral.white,
  paddingX: 2,
  paddingY: 3,
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '100%',
  width: '100%',
  boxSizing: 'border-box',
  '@media (min-width: 300px)': {
    paddingX: 2,
    paddingY: 4,
  },
  '@media (min-width: 600px)': {
    maxWidth: '500px',
    paddingX: 11,
    paddingY: 8,
  },
  ...appBorders.backrsDropShadowBorders.black,
} as const;

const responsiveBackrsLogoStyle: CSSProperties = {
  width: '100%',
  maxWidth: '175px',
  padding: '8px',
  marginBottom: '32px',
} as const;

const otpPromptStyle: SxProps<Theme> = {
  maxWidth: '300px',
  color: appColors.color.navy3,
  fontSize: '1.5rem',
  fontWeight: 700,
  textAlign: 'center',
  mb: 3,
} as const;

const otpContentWrapperStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
} as const;

const otpDecorationContainerStyle: SxProps<Theme> = {
  width: '100%',
  maxWidth: '350px',
  padding: 1,
} as const;

const otpInputStyle: SxProps<Theme> = {
  mb: 4,
} as const;

const otpInputProps: InputHTMLAttributes<HTMLInputElement> = {
  style: { textAlign: 'center', letterSpacing: '0.75rem', fontSize: '1.5rem' },
  maxLength: 6,
};
